import { useState } from 'react';
import { CropRect, EditorImageState, EditorTool, LassoPoint, SegmentationData } from './types';

export type UseImageEditorReturn = {
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  imageStates: EditorImageState[];
  currentState: EditorImageState | undefined;
  setActiveTool: (tool: EditorTool) => void;
  setCropRect: (rect: CropRect | undefined) => void;
  setLassoPoints: (points: LassoPoint[]) => void;
  setSegmentation: (data: SegmentationData | undefined) => void;
  toggleRegionSelection: (regionId: number) => void;
  setEditedFile: (file: File | undefined) => void;
  resetCurrentImage: () => void;
  getOutputFiles: () => File[];
};

const createInitialState = (file: File, initialCropRect?: CropRect): EditorImageState => ({
  originalFile: file,
  editedFile: undefined,
  activeTool: initialCropRect ? 'smartCrop' : 'segment',
  cropRect: initialCropRect,
  lassoPoints: [],
  segmentation: undefined,
  selectedRegionIds: new Set(),
});

export const useImageEditor = (files: File[], initialCropRect?: CropRect): UseImageEditorReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageStates, setImageStates] = useState<EditorImageState[]>(() =>
    files.map(file => createInitialState(file, initialCropRect))
  );

  const currentState = imageStates[currentIndex];

  const updateCurrentState = (updater: (state: EditorImageState) => EditorImageState) => {
    setImageStates(previous => previous.map((state, index) => (index === currentIndex ? updater(state) : state)));
  };

  const setActiveTool = (tool: EditorTool) => {
    updateCurrentState(state => ({
      ...state,
      activeTool: tool,
      editedFile: undefined,
      cropRect: undefined,
      lassoPoints: [],
      segmentation: undefined,
      selectedRegionIds: new Set(),
    }));
  };

  const setCropRect = (rect: CropRect | undefined) => {
    updateCurrentState(state => ({ ...state, cropRect: rect }));
  };

  const setLassoPoints = (points: LassoPoint[]) => {
    updateCurrentState(state => ({ ...state, lassoPoints: points }));
  };

  const setSegmentation = (data: SegmentationData | undefined) => {
    updateCurrentState(state => ({
      ...state,
      segmentation: data,
      selectedRegionIds: new Set(),
      editedFile: undefined,
    }));
  };

  const toggleRegionSelection = (regionId: number) => {
    updateCurrentState(state => {
      const next = new Set(state.selectedRegionIds);
      if (next.has(regionId)) {
        next.delete(regionId);
      } else {
        next.add(regionId);
      }
      return { ...state, selectedRegionIds: next };
    });
  };

  const setEditedFile = (file: File | undefined) => {
    updateCurrentState(state => ({ ...state, editedFile: file }));
  };

  const resetCurrentImage = () => {
    updateCurrentState(state => ({
      ...state,
      editedFile: undefined,
      cropRect: undefined,
      lassoPoints: [],
      selectedRegionIds: new Set(),
    }));
  };

  const getOutputFiles = () => imageStates.map(state => state.editedFile ?? state.originalFile);

  return {
    currentIndex,
    setCurrentIndex,
    imageStates,
    currentState,
    setActiveTool,
    setCropRect,
    setLassoPoints,
    setSegmentation,
    toggleRegionSelection,
    setEditedFile,
    resetCurrentImage,
    getOutputFiles,
  };
};
